import React, { useEffect, useState } from "react";
import { View, StyleSheet, Pressable, TextInput } from "react-native";
import { DatePickerInput } from "react-native-paper-dates";
import { XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSwitch from "../../../common/general/CustomSwitch/CustomSwitch";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import { StoragePathType } from "@axio/graphql/generated/graphql";
import type { ProcessedFile } from "../../../../hooks/useFileUpload";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

interface AssignmentGradingSectionProps {
  existingFeedbackFiles?: Array<{ id: string; fileURL: string }>;
  dueDate?: Date;
}

const validateGradeInput = (value: string) => {
  const valueAsNumber = Number(value);
  if (isNaN(valueAsNumber)) {
    return NaN;
  }
  return Math.max(Math.min(valueAsNumber, 100), 0);
};

const AssignmentGradingSection: React.FC<AssignmentGradingSectionProps> = ({ existingFeedbackFiles, dueDate }) => {
  const { updatedAssignmentPayload, setUpdatedAssignmentPayload } = useGradingCenterSubmissions();
  const [fileURLMap, setFileURLMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingFeedbackFiles) {
      setFileURLMap(Object.fromEntries(existingFeedbackFiles.map((f) => [f.id, f.fileURL])));
    }
  }, [existingFeedbackFiles]);

  const handleFeedbackFilesUploaded = (processedFiles: ProcessedFile[]) => {
    const newFiles = processedFiles.map(({ file, localUploadedFile }) => ({
      fileId: file.id,
      fileName: localUploadedFile.name,
    }));
    setFileURLMap((prev) => ({
      ...prev,
      ...Object.fromEntries(processedFiles.map(({ file }) => [file.id, file.fileURL])),
    }));
    setUpdatedAssignmentPayload((prev) => ({
      ...prev,
      feedbackFiles: [...(prev?.feedbackFiles ?? []), ...newFiles],
    }));
  };

  const handleRemoveFeedbackFile = (fileId: string) => {
    setUpdatedAssignmentPayload({
      ...updatedAssignmentPayload,
      feedbackFiles: updatedAssignmentPayload?.feedbackFiles?.filter((f) => f.fileId !== fileId) ?? [],
    });
  };

  return (
    <View style={styles.gradingSection}>
      <View style={styles.separator} />
      <View style={styles.gradingInputs}>
        <View style={styles.gradeInputContainer}>
          <CustomText text="Grade" size="s" weight="bold" style={styles.label} />
          <View style={styles.gradeInputBorder}>
            <TextInput
              keyboardType="numeric"
              value={updatedAssignmentPayload?.grade == null ? "" : updatedAssignmentPayload.grade.toString()}
              onChangeText={(value) => {
                const grade = validateGradeInput(value);
                if (isNaN(grade)) return;
                setUpdatedAssignmentPayload({
                  ...updatedAssignmentPayload,
                  grade: value === "" ? null : grade,
                });
              }}
              placeholder="Enter grade"
              placeholderTextColor={colors.grey[400]}
              style={styles.gradeInput}
            />
            <CustomText text="/ 100" size="s" style={styles.gradeSuffix} useTranslationText={false} />
          </View>

          <CustomText text="Enter a grade from 0 to 100" size="xs" style={styles.gradeHelp} />
        </View>

        <View style={styles.retakeOptionContainer}>
          <View style={styles.checkboxContainer}>
            <CustomSwitch
              value={updatedAssignmentPayload?.retakeAllowed ?? false}
              onValueChange={(val) =>
                setUpdatedAssignmentPayload({
                  ...updatedAssignmentPayload,
                  retakeAllowed: val,
                  retakeDeadline:
                    val && dueDate && new Date(dueDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
                      ? new Date(dueDate)
                      : val
                        ? new Date()
                        : null,
                })
              }
              trackColor={{ false: colors.grey[300], true: colors.brand[500] }}
              aria-label="Allow retake"
            />

            <CustomText text="Allow Retake" size="s" style={styles.checkboxLabel} />
          </View>

          {updatedAssignmentPayload?.retakeAllowed && (
            <View style={styles.retakeOptions}>
              <CustomText text="Retake Deadline" size="s" weight="bold" style={styles.label} />
              <DatePickerInput
                locale="en"
                dense={true}
                label={undefined}
                value={updatedAssignmentPayload?.retakeDeadline}
                onChange={(date) => setUpdatedAssignmentPayload({ ...updatedAssignmentPayload, retakeDeadline: date })}
                inputMode="start"
                mode="outlined"
                withDateFormatInLabel={false}
                outlineColor={"#D1D5DB"}
                activeOutlineColor={"#3B82F6"}
                placeholder="MM/DD/YYYY"
                theme={{ roundness: 8 }}
                style={styles.dateInput}
                aria-label="Retake deadline date picker"
                validRange={{
                  startDate:
                    dueDate && new Date(dueDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
                      ? new Date(dueDate)
                      : new Date(),
                }}
              />
            </View>
          )}
        </View>
      </View>

      <View style={styles.feedbackContainer}>
        <CustomText text="Feedback for Student (Optional)" size="s" weight="bold" style={styles.label} />
        <CustomTextInput
          value={updatedAssignmentPayload?.feedback}
          onChangeText={(value) => setUpdatedAssignmentPayload({ ...updatedAssignmentPayload, feedback: value })}
          placeholder="Provide feedback on the student's work..."
          multiline
          numberOfLines={8}
          maxLength={10000}
          style={styles.feedbackTextarea}
        />
        <CustomText
          text="character_count"
          size="xs"
          style={styles.feedbackCharCount}
          interpolation={{ value: updatedAssignmentPayload?.feedback?.length || 0 }}
        />
        <View style={styles.feedbackFilesContainer}>
          <CustomText text="Attach Feedback files" size="s" weight="bold" style={styles.label} />
          <CustomSpacing type="vertical" size="s" />
          <FileUploadComponent
            storagePath={StoragePathType.FeedbackAttachments}
            afterUpload={handleFeedbackFilesUploaded}
            multiple
          />
          {updatedAssignmentPayload?.feedbackFiles?.length > 0 && (
            <View style={styles.uploadedFilesList}>
              {updatedAssignmentPayload.feedbackFiles.map((file) => (
                <View key={file.fileId} style={styles.feedbackFileWrapper}>
                  <FileSubmissionItem
                    file={{ id: file.fileId, fileName: file.fileName, fileURL: fileURLMap[file.fileId] }}
                  />
                  <Pressable
                    onPress={() => handleRemoveFeedbackFile(file.fileId)}
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${file.fileName}`}
                    style={styles.removeButton}>
                    <XIcon size={16} color="#6B7280" />
                  </Pressable>
                </View>
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  gradingSection: {
    marginBottom: 32,
  },
  gradingInputs: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
    marginBottom: 24,
  },
  gradeInputContainer: {
    flex: 1,
  },
  gradeInputBorder: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: 10,
    backgroundColor: colors.white,
    marginTop: 6,
  },
  gradeInput: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
    // @ts-expect-error - web only
    outlineWidth: 0,
  },
  gradeSuffix: {
    color: colors.grey[500],
    marginLeft: spacing.xs,
  },
  gradeHelp: {
    color: colors.grey[500],
    marginTop: 6,
  },
  retakeOptionContainer: {
    alignItems: "flex-end",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  checkboxLabel: {
    color: "#374151",
  },
  retakeOptions: {
    marginTop: 12,
    paddingLeft: 28,
  },
  dateInput: {
    backgroundColor: "#FFFFFF",
    height: 42,
  },
  feedbackContainer: {
    flex: 1,
  },
  feedbackTextarea: {
    marginTop: 8,
    minHeight: 200,
    textAlignVertical: "top",
  },
  feedbackCharCount: {
    color: "#6B7280",
    textAlign: "right",
    marginTop: 6,
  },
  feedbackFilesContainer: {
    marginTop: 16,
  },
  label: {
    color: "#344054",
    fontWeight: "500",
  },
  uploadedFilesList: {
    marginTop: 12,
    gap: 8,
  },
  feedbackFileWrapper: {
    position: "relative",
  },
  removeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    padding: 4,
    zIndex: 1,
  },
  separator: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginBottom: 24,
  },
});

export default AssignmentGradingSection;
